/**
 * ThreadArchiveNoticeOverlay — the "someone is archiving this thread" screen
 * (owner directive 2026-08-26), in the workspace-change announcement's visual
 * language: opaque warm-black scrim, centered card, no page bleed-through.
 *
 * Display only. The thread store (useChatThreads) detects the remote archive,
 * publishes the notice through threadArchiveNotice.ts, holds the owner's five
 * seconds, performs the navigation to the next best thread, and clears the
 * notice — this component never decides anything, so the timing lives in
 * exactly one place.
 *
 * Unlike the workspace overlay this one ends on its own (the store's clear),
 * shows a visible countdown so the wait reads as deliberate rather than
 * frozen, and blocks interaction only for its five seconds — there is nothing
 * for the viewer to save, because the thread is archived for everyone and
 * Prest0n keeps it in memory either way.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { BODY_PORTAL_Z_INDEX } from './styles';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import BrandWordmark from '../BrandWordmark';
import {
  getThreadArchiveNotice,
  subscribeThreadArchiveNotice,
  THREAD_ARCHIVE_NOTICE_HOLD_MS,
  type ThreadArchiveNotice,
} from '../../services/threadArchiveNotice';

const overlayIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${BODY_PORTAL_Z_INDEX};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  /* Fully opaque, matching the workspace-change scrim: a half-archived
     conversation must never bleed through the announcement. */
  background: #100f0c;
  animation: ${overlayIn} 0.24s ease-out;
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  max-width: min(460px, calc(100vw - 48px));
  text-align: center;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--brand-canvas);
  animation: ${cardIn} 0.32s cubic-bezier(0.16, 1, 0.3, 1);
  &:focus {
    outline: none;
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Headline = styled.div`
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.5;
`;

const Detail = styled.div`
  font-size: 0.9rem;
  color: rgba(250, 250, 248, 0.75);
  line-height: 1.55;
`;

const Count = styled.div`
  font-size: 0.85rem;
  color: var(--brand-gold);
  font-variant-numeric: tabular-nums;
`;

export function ThreadArchiveNoticeOverlay(): JSX.Element | null {
  const [notice, setNotice] = useState<ThreadArchiveNotice | null>(() => getThreadArchiveNotice());
  const [secondsLeft, setSecondsLeft] = useState(Math.ceil(THREAD_ARCHIVE_NOTICE_HOLD_MS / 1000));

  useEffect(() => subscribeThreadArchiveNotice(setNotice), []);

  const blocking = notice?.kind === 'blocking';
  useBodyScrollLock(Boolean(blocking));

  useEffect(() => {
    if (!blocking) return undefined;
    setSecondsLeft(Math.ceil(THREAD_ARCHIVE_NOTICE_HOLD_MS / 1000));
    const timer = window.setInterval(() => {
      setSecondsLeft((prev) => (prev > 1 ? prev - 1 : prev));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [blocking, notice?.threadId]);

  if (!notice || !blocking) return null;
  if (typeof document === 'undefined') return null;

  const who = (notice.archivedByName || '').trim();
  const title = (notice.threadTitle || '').trim();

  return createPortal(
    <Scrim data-prest0n-portal="" data-thread-archive-notice="">
      <Card role="alertdialog" aria-live="assertive" tabIndex={-1}>
        <BrandWordmark />
        <Headline>
          {who ? `${who} is archiving this thread` : 'this thread is being archived'}
        </Headline>
        <Detail>
          {title ? `"${title}" ` : 'it '}
          is closing for everyone in it — <KeepName>Prest0n</KeepName> keeps the conversation in
          memory for reference. you&apos;ll land on your next thread in a moment.
        </Detail>
        <Count>{secondsLeft}</Count>
      </Card>
    </Scrim>,
    document.body,
  );
}

/** "Prest0n" stays capitalized whatever text-transform an ancestor carries. */
const KeepName = styled.span`
  text-transform: none;
`;

export default ThreadArchiveNoticeOverlay;
